import logger from '@redbubble/next/logger';
import { isEqual, uniqBy } from 'lodash';
import { InventoryItem, InventoryItemExperience } from 'types/shop-bff/types';
import {
  AttributeName,
  type AttributesContainer,
  type ConfiguratorField,
  type ConfiguratorValues,
  type FlatInventoryItemConfiguratorOption,
  type FormikValues,
  type InventoryItemAttribute,
  type PartialCategorySet,
  PRIMARY_ATTRIBUTE_NAMES,
  type SimpleAttribute,
} from './types';

interface OrderItem {
  [AttributeName.Order]?: number;
}
export type CategoryItemAttribute = AttributesContainer['attributes'][number];

const isString = (a: unknown): a is string => typeof a === 'string';
const isNumber = (a: unknown): a is number => typeof a === 'number';

const resolveDisplayOrder = (orderItem: OrderItem) => {
  const value = orderItem.displayOrder;
  if (isNumber(value) && Number.isFinite(value)) return value;
  return Number.POSITIVE_INFINITY;
};

/**
 * Compares objects with an optional `displayOrder` property.
 *
 * For objects with numeric `displayOrder`, returns the comparison as the
 * difference between the 2 values.
 *
 * For objects with non-numeric `displayOrder`, will will return +infinity if comparing against
 * a number, or 0 if comparing two non numeric `displayOrder`s
 */
export const compareDisplayOrder = (a: OrderItem, b: OrderItem): number => {
  const orderA = resolveDisplayOrder(a);
  const orderB = resolveDisplayOrder(b);
  const comparison = orderA - orderB;

  if (Number.isNaN(comparison)) return 0;

  return comparison;
};

const getValidatedAttributeValueFromAttributes = <T = unknown>(
  attributeName: AttributeName,
  attributes: Array<SimpleAttribute>,
  validator: (a) => a is T
) => {
  const matchedAttributes = attributes.filter((a) => a.name === attributeName);

  if (matchedAttributes.length !== 1) return undefined;

  const attribute = matchedAttributes[0];

  return validator(attribute.value) ? attribute.value : undefined;
};

const getDefaultText = (attributes: Array<SimpleAttribute>) => {
  return getValidatedAttributeValueFromAttributes(
    AttributeName.Label,
    attributes,
    isString
  );
};

const getDisplayOrder = (attributes: Array<SimpleAttribute>) => {
  return getValidatedAttributeValueFromAttributes(
    AttributeName.Order,
    attributes,
    isNumber
  );
};

export const isAttributeInAttributeContainer = (
  { name, value }: SimpleAttribute,
  attributeContainer: { attributes: Array<SimpleAttribute> }
) =>
  attributeContainer.attributes.some(
    (attr) => attr.name === name && attr.value === value
  );

/**
 * The categorySet.configurationSet property contains data about all available Attributes,
 * and metadata for all options for each attribute, in one flat array.
 * 
 * The ProductConfigurator component requires "ConfiguratorField" 
 * to render fields. "ConfiguratorField" are objects containing all necessary
 * data to render a configuration field.
 * 
 * This function extracts all available "ConfiguratorField" from categorySet.configurationSet.
 * 
 *  * e.g. input from categorySet.configurationSet:
 * [
 *    { name: "configuration", value: "size" },
 *    { name: "configuration", value: "color" },
 *    { name: "size", value: "xl" },
 *    { name: "size", value: "xxl" },
 *    { name: "color", value: "blue" },
 *    { name: "color", value: "green" },
 * ]
 * 
 * returns an array of available ConfiguratorField:
 * 
 * e.g. 
  [
    {
      attributeName: "size",
      options: [
        {
          name: "size",
          value: "blue",
        },
        {
          name: "size",
          value: "green",
        },
        {
          name: "...",
          ...
        },
        ...
    },
    {
      attributeName: "..."
      ...
    },
    ...
  ]
 * @param categorySet 
 * @returns ConfigurationAttribute[]
 */

const getFallbackOptions = (
  configOptionsFromItems: Array<CategoryItemAttribute>,
  inventoryItem: InventoryItem
) => {
  const inventoryItemAttributes = inventoryItem.attributes;

  // Check if all config options for an attribute are the same
  const hasConflictingOptionsAndNestedAttributes = configOptionsFromItems.every(
    (attr) =>
      attr.value === configOptionsFromItems[0].value &&
      attr.name === configOptionsFromItems[0].name &&
      !!attr.attributes
  );

  if (hasConflictingOptionsAndNestedAttributes) {
    const currentInventoryItemOptions = inventoryItemAttributes.find(
      (attr) => attr.name === configOptionsFromItems[0].name
    );

    if (!currentInventoryItemOptions) {
      const message = `Product Configurator found no matching config option on inventory item ${inventoryItem.id} for ${configOptionsFromItems[0].name}`;
      logger.warn({ message, context: 'getFallbackOptions' }, message);
      return uniqBy(configOptionsFromItems, (option) => option.value);
    }

    const result = configOptionsFromItems.find((option) =>
      isEqual(currentInventoryItemOptions.attributes, option.attributes)
    );

    if (!result) {
      const message = `Product Configurator found matching config option on inventory item ${inventoryItem.id} for ${configOptionsFromItems[0].name}, but attributes were not equal`;
      logger.warn({ message, context: 'getFallbackOptions' }, message);
      return uniqBy(configOptionsFromItems, (option) => option.value);
    }

    return [result];
  }

  return uniqBy(configOptionsFromItems, (option) => option.value);
};

export const parseAttributesFromCategorySet: (
  categorySet: PartialCategorySet,
  currentInventoryItem: InventoryItem
) => Array<ConfiguratorField> = (categorySet, currentInventoryItem) => {
  const { defaultItem } = categorySet;

  return categorySet.configurationSet
    .filter((a) => a.name === AttributeName.Configuration)
    .map(({ attributes, value: attributeName }) => {
      const defaultItemOptions = defaultItem.attributes.filter(
        ({ name }) => name === attributeName
      );

      const configOptionsFromItems = categorySet.items
        ? categorySet.items.flatMap((item) => {
            return item.attributes.filter(
              (attr) => attr.name === attributeName
            );
          })
        : [];

      if (configOptionsFromItems.length === 0)
        return {
          name: attributeName,
          defaultText: getDefaultText(attributes),
          displayOrder: getDisplayOrder(attributes),
          options: defaultItemOptions,
        };

      const parsedOptions = getFallbackOptions(
        configOptionsFromItems,
        currentInventoryItem
      );

      return {
        name: attributeName,
        defaultText: getDefaultText(attributes),
        displayOrder: getDisplayOrder(attributes),
        options:
          parsedOptions.length === 0 ? defaultItemOptions : parsedOptions,
      };
    });
};

/**
 * Given a ConfiguratorField and an array of items AttributeContainers (typically inventoryItems),
 * filter out all options from the ConfiguratorField, which does not have matching attributes
 * from any of the supplied attribute containers OR is one of the PRIMARY config option:
 *
 * e.g.
 * ConfiguratorField : {
 *   options:[
 *     {name:"size", value:"xl"},
 *     {name:"size", value:"m"},
 *     {name:"size", value:"s"},
 *   ]
 * }
 *
 * availableItems: [
 *   { attributes:[{name:"size", value:"xl"}]},
 *   { attributes:[{name:"size", value:"m"}]},
 * ]
 *
 * returns:
 * ConfiguratorField : {
 *   options:[
 *     {name:"size", value:"xl"},
 *     {name:"kind", value:"m"},
 *     // attribute with size = S removed as it is not present in availableItems
 *   ]
 * } *
 *
 * e.g. With primary config option
 *
 * e.g.
 * ConfiguratorField : {
 *   options:[
 *     {name:"size", value:"xl"},
 *     {name:"size", value:"m"},
 *     {name:"size", value:"s"},
 *     {name:"model", value:"iphone_14"},
 *   ]
 * }
 *
 * availableItems: [
 *   { attributes:[{name:"size", value:"xl"}]},
 *   { attributes:[{name:"size", value:"m"}]},
 * ]
 *
 * returns:
 * ConfiguratorField : {
 *   options:[
 *     {name:"size", value:"xl"},
 *     {name:"size", value:"m"},
 *     {name:"model", value:"iphone_14"}, // model stays because it is a Primary Attribute
 *     // attribute with size = S removed as it is not present in availableItems
 *   ]
 * }
 */
export const filterConfigurationAttributeAvailableOptions = (
  configurationAttribute: ConfiguratorField,
  availableItems: Array<AttributesContainer>
): ConfiguratorField => {
  const filteredOptions = configurationAttribute.options.filter(
    (attribute) =>
      availableItems.some((item) =>
        isAttributeInAttributeContainer(attribute, item)
      ) || PRIMARY_ATTRIBUTE_NAMES.includes(attribute.name)
  );
  return {
    ...configurationAttribute,
    options: filteredOptions,
  };
};
/**
 * While handling attribute values, it is safer to use an array of key-value pairs
 * because if we pass around an object map, which is what the design system form requires
 * e.g. {"size:":"large", "color":"green"}
 *
 * we run into risks enumerating over the keys of the object.
 * This is because Object.keys may return unwanted keys from the object under certain circumstances.
 *
 * This is why we require an "attributes" parameter, to act as the "source of truth", for the list of attributes
 * when we extract the values from the object map.
 */
export const formikValuesToConfiguratorValues = (
  attributeNames: Array<{ name: string }>,
  valueMap: FormikValues
): ConfiguratorValues => {
  return attributeNames.map(({ name }) => ({ name, value: valueMap[name] }));
};

/**
 * The Design system form wants it's state value in the form of
 * {
 * "size":"large",
 * "color":"green",
 * }
 *
 * This function converts from the ConfiguratorValues, into the object map form.
 */
export const configuratorValuesToFormikValues = (
  attributeCollection: ConfiguratorValues
): FormikValues => {
  return attributeCollection.reduce(
    (acc, { name, value }) => ({ ...acc, [name]: value }),
    {}
  );
};

export const getFieldsRequiringATCGuard = (
  experiences: Readonly<Array<InventoryItemExperience>>,
  currentItemAttributes?: SimpleAttribute[]
): string[] => {
  const atcFields = experiences.reduce((result: string[], item) => {
    if (AttributeName.ATCGuardExperience === item.name) {
      result.push(item.value);
    }
    return result;
  }, []);

  /**
   * DEP-3038: https://redbubble.atlassian.net/browse/DEP-3038
   * This is a tactical measure to render ATC Guard for model attribute. We want
   * the guidance to come from upstreat – PTR -> Shop BFF.
   */
  if (
    currentItemAttributes?.some((attr) => {
      return attr.name === AttributeName.Model;
    })
  )
    return [...atcFields, AttributeName.Model];

  return atcFields;
};

/**
 * for a given key-value attribute array,
 * e.g. {key:string, value:string}[]
 *
 * we flatten this array into an object in the form of
 * e.g. {
 *   key:string,
 *   ...
 * }
 */
export const flattenOptionAttributes = (
  attribute: InventoryItemAttribute
): FlatInventoryItemConfiguratorOption => ({
  value: attribute.value,
  ...attribute.attributes.reduce(
    (a, value) => ({ ...a, [value.name]: value.value }),
    {}
  ),
});

/**
 * for a given optional defaultText, return the defaultText,
 * if present or return the given fallback.
 *
 * If the fallback value is used, the logger will warn us about
 * the use of the fallback value, using the `attributeName` parameter
 * to add more detail to the warning message.
 */
export const getFallbackDefaultText = ({
  attributeName,
  defaultText,
  fallback,
}: {
  attributeName: string;
  fallback: string;
  defaultText: any;
}): string => {
  if (typeof defaultText !== 'string' || defaultText.length === 0) {
    // TODO [DEP-2223]: How can we make this better? captured in this card https://redbubble.atlassian.net/browse/DEP-2223
    const message = `Failed to get defaultText for ${attributeName}`;
    logger.warn(
      { message, context: 'getFallbackOptions', attributeName },
      message
    );

    return fallback;
  }
  return defaultText;
};

// DEP-2801: Phone cases for settings instructions
export const SHOW_INSTRUCTIONS_FOR_IPHONE_PRODUCT_TYPES = [
  'rough-sultana',
  'lovely-dingo',
  'cranky-beetroot',
  'whinging-rissole',
  'wagging-grub',
  'ferric-fortifier',
];

export const SHOW_INSTRUCTIONS_FOR_SAMSUNG_PRODUCT_TYPES = [
  'bush-brolly',
  'bush-magpie',
  'furphy-fiddlesticks',
  'tassie-wallaroo',
];
